import { DateTime } from 'luxon';

import { ApproveByDate, FiscalYearEnd } from 'smg/approve-by';
import { EveryLocalMidnight, FormatLongDate } from 'smg/dates';
import { SetShown } from 'smg/dom';
import { Download } from 'smg/download';
import { AllDayEvent } from 'smg/ics';

// The field is the browser's date input. Its value is yyyy-mm-dd, or empty.
// A half-typed date is validity.badInput, and a day before today is
// validity.rangeUnderflow against min. Either message shows only after blur or
// Enter, because typing a year passes through years long gone.
export function MountApproveByCalculators(root: ParentNode): void {
    for (const $root of root.querySelectorAll<HTMLElement>('[data-approve-by]')) {
        mountCalculator($root);
    }
}

function mountCalculator($root: HTMLElement): void {
    const $input = $root.querySelector<HTMLInputElement>('[data-approve-by-input]');
    const $fiscal = $root.querySelector<HTMLButtonElement>('[data-approve-by-fiscal]');
    const $result = $root.querySelector<HTMLElement>('[data-approve-by-result]');
    const $date = $root.querySelector<HTMLElement>('[data-approve-by-date]');
    const $tooClose = $root.querySelector<HTMLElement>('[data-approve-by-too-close]');
    const $calendar = $root.querySelector<HTMLButtonElement>('[data-approve-by-calendar]');
    const $hint = $root.querySelector<HTMLElement>('[data-approve-by-hint]');
    const $error = $root.querySelector<HTMLElement>('[data-approve-by-error]');
    const $past = $root.querySelector<HTMLElement>('[data-approve-by-past]');
    if (!$input || !$fiscal || !$result || !$date || !$tooClose || !$calendar || !$hint || !$error || !$past) {
        return;
    }
    const leadDays = Number($root.dataset.leadDays ?? '');
    if (!Number.isFinite(leadDays)) {
        return;
    }
    const calendarTitle = $root.dataset.calendarTitle ?? '';
    const calendarDescription = $root.dataset.calendarDescription ?? '';

    // A page left open past midnight still plans from the day it is read.
    const today = () => DateTime.now().startOf('day');

    // The date the result shows, which Add to Calendar saves. Null unless the
    // result is on screen.
    let shown: { needed: DateTime; approveBy: DateTime } | null = null;

    // The invalid and past-date copy waits for blur or Enter, so a date being
    // typed is never called wrong.
    let invalidCommitted = false;
    const commit = () => {
        invalidCommitted = $input.validity.badInput || $input.validity.rangeUnderflow;
    };

    const paint = () => {
        $input.min = today().toISODate() ?? '';
        const badInput = $input.validity.badInput;
        const past = $input.validity.rangeUnderflow;
        const showInvalid = invalidCommitted && badInput;
        const showPast = invalidCommitted && past;
        const needed = badInput || past || $input.value === '' ? null : DateTime.fromISO($input.value).startOf('day');
        const approveBy = needed?.isValid ? ApproveByDate(needed, leadDays) : null;
        const tooClose = approveBy !== null && approveBy < today();
        const showResult = approveBy !== null && !tooClose;
        shown = showResult && needed ? { needed, approveBy } : null;
        let $message = $hint;
        if (showInvalid) {
            $message = $error;
        } else if (showPast) {
            $message = $past;
        }
        $input.classList.toggle('is-invalid', $message !== $hint);
        $input.setAttribute('aria-invalid', $message !== $hint ? 'true' : 'false');
        $input.setAttribute('aria-describedby', $message.id);
        SetShown($hint, $message === $hint);
        SetShown($error, $message === $error);
        SetShown($past, $message === $past);
        SetShown($result, showResult);
        SetShown($tooClose, tooClose);
        if (showResult && approveBy) {
            $date.textContent = FormatLongDate(approveBy);
        }
    };

    paint();

    $input.addEventListener('blur', () => {
        commit();
        paint();
    });
    $input.addEventListener('keydown', event => {
        if (event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        commit();
        paint();
    });
    $input.addEventListener('focus', () => {
        invalidCommitted = false;
        paint();
    });
    $input.addEventListener('input', () => {
        invalidCommitted = false;
        paint();
    });
    $input.addEventListener('change', () => {
        if (!$input.validity.badInput) {
            invalidCommitted = false;
        }
        paint();
    });

    $fiscal.addEventListener('click', () => {
        $input.value = FiscalYearEnd(today()).toISODate() ?? '';
        invalidCommitted = false;
        paint();
    });

    $calendar.addEventListener('click', () => {
        if (!shown) {
            return;
        }
        const { needed, approveBy } = shown;
        const neededLabel = needed.toLocaleString({ year: 'numeric', month: 'long', day: 'numeric' });
        const description = calendarDescription.replaceAll('{needed date}', neededLabel);
        const ics = AllDayEvent({
            prodId: '-//All About//Approve-by date//EN',
            uid: `${approveBy.toISODate() ?? ''}-approve-by@${location.hostname}`,
            date: approveBy,
            title: calendarTitle,
            description,
        });
        Download(`approve-all-about-order-${approveBy.toFormat('yyyyMMdd')}.ics`, ics, 'text/calendar');
    });

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && $root.isConnected) {
            paint();
        }
    });
    EveryLocalMidnight(() => {
        if ($root.isConnected) {
            paint();
        }
    });
}
